import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowRight } from "lucide-react";
import { useText } from "../contexts/TextsContext";
import { trackFormSubmit } from "../lib/tracking";

export default function CustomRequest() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const t = useText();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "",
    surface_needed: "", activity: "", message: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact-requests", {
        ...form,
        company: form.company || null,
        surface_needed: form.surface_needed || null,
        activity: form.activity || null,
      });
      trackFormSubmit("custom_request", {
        surface_needed: form.surface_needed || null,
        activity: form.activity || null,
      });
      toast.success(t("custom.success"));
      navigate("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-4xl mx-auto px-6 md:px-8 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-4">{t("custom.kicker")}</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-4">{t("custom.title")}</h1>
        <p className="text-slate-600 max-w-2xl mb-10">{t("custom.subtitle")}</p>

        <form onSubmit={submit} className="bg-white border border-slate-900/15 p-8 md:p-10 space-y-5" data-testid="custom-request-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={t("custom.f.name")}><input required value={form.full_name} onChange={set("full_name")} className="input" data-testid="cr-name" /></Field>
            <Field label={t("custom.f.email")}><input required type="email" value={form.email} onChange={set("email")} className="input" data-testid="cr-email" /></Field>
            <Field label={t("custom.f.phone")}><input required value={form.phone} onChange={set("phone")} className="input" data-testid="cr-phone" /></Field>
            <Field label={t("custom.f.company")}><input value={form.company} onChange={set("company")} className="input" /></Field>
            <Field label={t("custom.f.surface")}><input placeholder={t("custom.f.surface_ph")} value={form.surface_needed} onChange={set("surface_needed")} className="input" /></Field>
            <Field label={t("custom.f.activity")}><input placeholder={t("custom.f.activity_ph")} value={form.activity} onChange={set("activity")} className="input" /></Field>
          </div>
          <Field label={t("custom.f.message")}>
            <textarea required rows={5} value={form.message} onChange={set("message")} className="input resize-none" data-testid="cr-message"
              placeholder={t("custom.f.message_ph")} />
          </Field>
          <button disabled={submitting} type="submit" data-testid="submit-custom-request"
            className="btn-brutal btn-brutal-solar w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-3 disabled:opacity-50">
            {submitting ? t("global.cta_sending") : t("custom.submit")} {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
